import React, { useState, useEffect } from "react";
import {
  FaGraduationCap,
  FaLanguage,
  FaAward,
  FaBookOpen,
  FaGlobe,
  FaStar,
} from "react-icons/fa";

const education = [
  {
    year: "2005",
    title: "Secondary Education",
    detail: "Durbar High School (Sanskrit Secondary School)",
    icon: FaBookOpen,
    color: "from-blue-500 to-cyan-500",
  },
  {
    year: "2008",
    title: "+2 Science",
    detail: "Blue Bird College",
    icon: FaGraduationCap,
    color: "from-green-500 to-emerald-500",
  },
  {
    year: " 2011",
    title: "B.Sc. Bachelor of Science",
    detail: " Tri Chandra (Tribhuvan University)",
    icon: FaAward,
    color: "from-purple-500 to-violet-500",
  },
  {
    year: "BA (Sastri - 2011) & MA (Acharya - 2013)",
    title: "Sanskrit Studies",
    detail: "Nepal Sanskrit University",
    icon: FaBookOpen,
    color: "from-orange-500 to-red-500",
  },
  {
    year: "2010–2012",
    title: "Spanish Language",
    detail: "Language Training Program (Tribhuvan University)",
    icon: FaLanguage,
    color: "from-pink-500 to-rose-500",
  },
  {
    year: "Chinese (Basic)",
    title: "Mandarin Chinese",
    detail: "Tribhuvan University",
    icon: FaGlobe,
    color: "from-indigo-500 to-blue-500",
  },
  {
    year: "French (Basic)",
    title: "French Language",
    detail: "Basic Level Certification",
    icon: FaLanguage,
    color: "from-teal-500 to-cyan-500",
  },
];

const languages = [
  { name: "English", level: "Fluent", progress: 95, color: "bg-blue-500" },
  { name: "Spanish", level: "Fluent", progress: 90, color: "bg-red-500" },
  {
    name: "Chinese",
    level: "Basic",
    progress: 20,
    color: "bg-yellow-500",
  },
  { name: "French", level: "Basic", progress: 30, color: "bg-purple-500" },
  { name: "Hindi", level: "Fluent", progress: 95, color: "bg-orange-500" },
  { name: "Sanskrit", level: "Advanced", progress: 85, color: "bg-green-500" },
];

const achievements = [
  {
    icon: FaGraduationCap,
    number: "7+",
    label: "Qualifications",
    description: "Academic and Professional",
  },
  {
    icon: FaLanguage,
    number: "6+",
    label: "Languages",
    description: "Multilingual Expert",
  },
  {
    icon: FaGlobe,
    number: "15+",
    label: "Years",
    description: "Industry Experience",
  },
  {
    icon: FaStar,
    number: "50+",
    label: "Countries",
    description: "Global Network",
  },
];

export default function About() {
  const [visibleItems, setVisibleItems] = useState([]);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => [...prev, entry.target.dataset.index]);
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".timeline-item").forEach((item, index) => {
      item.dataset.index = index;
      observer.observe(item);
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Hero Section */}
      <div className="relative pt-24 pb-16 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-6xl md:text-8xl font-black mb-6 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              About Me
            </h1>
            <div className="w-32 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto mb-8 rounded-full"></div>
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Born on 17 May 1990 in Sindhupalchok, Nepal. With a diverse
              background in Sanskrit studies, science, and languages, I've built
              a successful career connecting cultures through travel and
              education.
            </p>
          </div>

          {/* Achievement Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {achievements.map((achievement, index) => (
              <div
                key={index}
                className="group relative bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 hover:border-purple-500/50 transition-all duration-500 hover:scale-105 text-center"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="text-purple-400 mb-4 flex justify-center group-hover:scale-110 transition-transform duration-300">
                    <achievement.icon className="w-8 h-8" />
                  </div>
                  <div className="text-4xl font-bold text-white mb-2">
                    {achievement.number}
                  </div>
                  <div className="text-lg font-semibold text-purple-300 mb-2">
                    {achievement.label}
                  </div>
                  <div className="text-sm text-gray-400">
                    {achievement.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Education Timeline */}
      <div className="px-6 lg:px-20 py-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Educational Journey
            </h2>
            <p className="text-xl text-gray-300">
              A lifelong commitment to learning and growth
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500 rounded-full"></div>

            {/* Timeline Items */}
            <div className="space-y-16">
              {education.map((edu, index) => (
                <div
                  key={index}
                  className={`timeline-item relative flex ${
                    index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  } flex-col transition-all duration-1000 ${
                    visibleItems.includes(index.toString())
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-8"
                  }`}
                >
                  {/* Content Card */}
                  <div
                    className={`w-full md:w-5/12 ${
                      index % 2 === 0
                        ? "md:text-right md:pr-8"
                        : "md:text-left md:pl-8"
                    } mb-6 md:mb-0`}
                  >
                    <div className="group bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 hover:scale-105">
                      <div className="flex items-center mb-3">
                        <div
                          className={`w-12 h-12 bg-gradient-to-r ${edu.color} rounded-full flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300`}
                        >
                          <edu.icon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <div className="text-lg font-bold text-white">
                            {edu.title || edu.year}
                          </div>
                          <div className="text-sm text-purple-300">
                            {edu.year !== edu.title ? edu.year : ""}
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-300">{edu.detail}</p>
                    </div>
                  </div>

                  {/* Timeline Node (hide on mobile, keep on md+) */}
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full border-4 border-slate-900 z-10 group-hover:scale-125 transition-transform duration-300"></div>

                  {/* Empty Space (only for desktop symmetry) */}
                  <div className="hidden md:block w-5/12"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Languages Section */}
      <div className="px-6 lg:px-20 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Language Expertise
            </h2>
            <p className="text-xl text-gray-300">
              Connecting cultures through multilingual communication
            </p>{" "}
            <p className="text-xl text-gray-300">
              Greeting the world in 10+ languages, bridging cultures
              effortlessly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {languages.map((lang, index) => (
              <div
                key={index}
                className="group bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white group-hover:text-purple-300 transition-colors">
                    {lang.name}
                  </h3>
                  <span className="text-sm font-medium text-gray-400 px-3 py-1 bg-white/10 rounded-full">
                    {lang.level}
                  </span>
                </div>

                <div className="relative">
                  <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-full ${lang.color} rounded-full transition-all duration-1000 ease-out`}
                      style={{
                        width: visibleItems.includes(index.toString())
                          ? `${lang.progress}%`
                          : "0%",
                      }}
                    ></div>
                  </div>
                  <div className="text-right mt-2">
                    <span className="text-sm font-medium text-purple-300">
                      {lang.progress}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Personal Quote */}
      <div className="px-6 lg:px-20 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-12 border border-white/10">
            <blockquote className="text-2xl md:text-3xl font-light text-gray-200 italic leading-relaxed mb-6">
              "Education is not just about acquiring knowledge; it's about
              building bridges between cultures and creating meaningful
              connections that transcend boundaries."
            </blockquote>
            <div className="w-16 h-1 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto rounded-full"></div>
          </div>
        </div>
      </div>

      {/* Background Elements */}
      <div className="fixed inset-0 pointer-events-none opacity-10">
        <div
          className="absolute w-96 h-96 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full blur-3xl"
          style={{
            transform: `translate(${scrollY * 0.1}px, ${scrollY * 0.05}px)`,
            left: "10%",
            top: "20%",
          }}
        ></div>
        <div
          className="absolute w-96 h-96 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-3xl"
          style={{
            transform: `translate(-${scrollY * 0.1}px, -${scrollY * 0.05}px)`,
            right: "10%",
            bottom: "20%",
          }}
        ></div>
      </div>
    </div>
  );
}
